// pages/settings-v2.js — the local Settings page.
//
// Renders entirely from GET /v1/config/schema: the daemon's spec table
// (internal/config/settings_schema.go) owns every label, description,
// tooltip, default, limit and "takes effect" note, so the page cannot drift
// from what the daemon actually enforces. Each change is saved on its own
// (PUT /v1/config with just that key) and confirmed inline, with an Undo.
//
// Links can open one setting: /settings#<tab> or /settings#<setting_key>
// (openSettingsAt for in-app links that navigate first).

import { agentLogo } from "../agent-logos.js";

const SOURCE_NOTE = {
  repo: "Set by this repo's .agentcollision/config.toml, so it can't be changed here.",
  team: "Locked by your team. A team admin can change it in Team settings.",
  "machine-remote": "Set for this machine from the hosted dashboard. Change it there.",
};

let root = null;
let schema = null;
let tools = null; // GET /v1/tools (null when unavailable)
let info = null; // GET /v1/info: team sync health
let toolNotes = {}; // id or id|root -> one-time step shown after connecting
let toolBusy = {};
let showOtherTools = false;
let tab = "tools";
let query = "";
let target = (location.hash || "").replace("#", ""); // tab or setting key to open next
let loadedValues = {}; // what the running daemon started with, for "needs restart"
let undo = null; // { row, prev, timer } for the last change
let restarting = null; // null | "working" | error message

function h(tag, attrs, ...kids) {
  const el = document.createElement(tag);
  if (attrs) {
    for (const [k, v] of Object.entries(attrs)) {
      if (v == null || v === false) continue;
      if (k === "class") el.className = v;
      else if (k === "text") el.textContent = v;
      else if (k.startsWith("on")) el.addEventListener(k.slice(2), v);
      else el.setAttribute(k, v === true ? "" : v);
    }
  }
  for (const c of kids.flat(Infinity)) {
    if (c == null || c === false) continue;
    el.appendChild(typeof c === "string" ? document.createTextNode(c) : c);
  }
  return el;
}

async function request(method, path, body) {
  const r = await fetch(path, {
    method,
    headers: body ? { "Content-Type": "application/json" } : undefined,
    body: body ? JSON.stringify(body) : undefined,
  });
  let j = null;
  try { j = await r.json(); } catch { /* empty body */ }
  if (!r.ok) {
    const msg = (j && (j.message || (j.error && j.error.message) || j.error)) || r.status + "";
    throw new Error(typeof msg === "string" ? msg : JSON.stringify(msg));
  }
  return j;
}

function tip(text) {
  return h("span", { class: "sv-tip", tabindex: "0", role: "img", "aria-label": text, "data-tip": text }, "?");
}

// humanDuration turns 1800 seconds into "30 min" so nobody does the maths.
function humanDuration(n, unit) {
  if (!n || (unit !== "seconds" && unit !== "hours")) return "";
  const secs = unit === "hours" ? n * 3600 : n;
  if (secs < 120) return "";
  const parts = [];
  const d = Math.floor(secs / 86400), hrs = Math.floor((secs % 86400) / 3600), min = Math.floor((secs % 3600) / 60);
  if (d) parts.push(d + (d === 1 ? " day" : " days"));
  if (hrs) parts.push(hrs + " h");
  if (min && !d) parts.push(min + " min");
  return parts.join(" ");
}

function fmtNumber(n) {
  return Number(n).toLocaleString();
}

function isLocked(row) {
  return row.read_only || !!SOURCE_NOTE[row.source];
}

function matches(row) {
  if (!query) return true;
  const hay = (row.label + " " + row.desc + " " + (row.tip || "") + " " + row.key).toLowerCase();
  return hay.includes(query);
}

// ---- controls ----

function control(row, onChange) {
  const locked = isLocked(row);
  if (row.read_only && row.key === "dashboard_port") {
    return h("code", { class: "sv-value" }, String(row.value || "not serving"));
  }
  switch (row.type) {
    case "bool": {
      const sw = h("button", {
        type: "button", class: "sv-switch", role: "switch", "aria-label": row.label,
        "aria-checked": row.value ? "true" : "false", disabled: locked,
        onclick: () => onChange(!(sw.getAttribute("aria-checked") === "true")),
      });
      return sw;
    }
    case "enum": {
      return h("div", { class: "sv-seg", role: "radiogroup", "aria-label": row.label },
        (row.options || []).map((o) => h("button", {
          type: "button", role: "radio", class: row.value === o.value ? "on" : null,
          "aria-checked": row.value === o.value ? "true" : "false", disabled: locked,
          onclick: () => { if (row.value !== o.value) onChange(o.value); },
        }, o.label)));
    }
    case "int": {
      const input = h("input", {
        type: "number", class: "sv-num", value: String(row.value), disabled: locked,
        min: row.min != null ? String(row.min) : null, max: row.max != null ? String(row.max) : null,
        "aria-label": row.label,
      });
      const commit = () => {
        const n = parseInt(input.value, 10);
        if (!Number.isFinite(n)) { input.value = String(row.value); return; }
        if (row.min != null && n < row.min) return showRowError(row.key, "The lowest allowed value is " + fmtNumber(row.min) + ".");
        if (row.max != null && n > row.max) return showRowError(row.key, "The highest allowed value is " + fmtNumber(row.max) + ".");
        if (n !== row.value) onChange(n);
      };
      input.addEventListener("change", commit);
      input.addEventListener("keydown", (e) => { if (e.key === "Enter") input.blur(); });
      const human = humanDuration(row.value, row.unit);
      return h("span", { class: "sv-num-wrap" }, input, row.unit ? h("span", { class: "sv-unit", text: row.unit }) : null,
        human ? h("span", { class: "sv-human", text: "= " + human }) : null);
    }
    default: {
      const input = h("input", { type: "text", class: "sv-text", value: row.value || "", disabled: locked, "aria-label": row.label, spellcheck: "false" });
      input.addEventListener("change", () => { if (input.value.trim() !== (row.value || "")) onChange(input.value.trim()); });
      input.addEventListener("keydown", (e) => { if (e.key === "Enter") input.blur(); });
      return input;
    }
  }
}

function rowMeta(row) {
  const bits = [];
  const changed = row.source === "machine" && JSON.stringify(row.value) !== JSON.stringify(row.default) && !row.read_only && !row.machine;
  if (changed) {
    bits.push(h("span", { class: "sv-pill" }, "Changed"));
    bits.push(h("button", { type: "button", class: "sv-link", onclick: () => save(row, row.default) }, "Reset to " + describeValue(row, row.default)));
  }
  if (row.type === "int" && row.zero_means && row.value === 0) bits.push(h("span", { class: "sv-hint", text: "0 = " + row.zero_means }));
  if (row.applies === "restart" && !row.read_only) bits.push(h("span", { class: "sv-hint", text: "Takes effect after the daemon restarts" }));
  if (SOURCE_NOTE[row.source]) bits.push(h("span", { class: "sv-lock" }, lockIcon(), SOURCE_NOTE[row.source]));
  return bits.length ? h("div", { class: "sv-meta" }, bits) : null;
}

function describeValue(row, v) {
  if (row.type === "bool") return v ? "on" : "off";
  if (row.type === "enum") { const o = (row.options || []).find((x) => x.value === v); return o ? o.label : String(v); }
  if (row.type === "int") return v === 0 && row.zero_means ? row.zero_means : fmtNumber(v) + (row.unit ? " " + row.unit : "");
  return v === "" ? "empty" : String(v);
}

function lockIcon() {
  const s = document.createElementNS("http://www.w3.org/2000/svg", "svg");
  s.setAttribute("viewBox", "0 0 16 16"); s.setAttribute("width", "12"); s.setAttribute("height", "12");
  s.innerHTML = '<rect x="3.5" y="7" width="9" height="6.5" rx="1.5" fill="none" stroke="currentColor" stroke-width="1.6"/><path d="M5.5 7V5a2.5 2.5 0 015 0v2" fill="none" stroke="currentColor" stroke-width="1.6"/>';
  return s;
}

function settingRow(row) {
  return h("div", { class: "sv-row" + (isLocked(row) ? " locked" : ""), "data-key": row.key },
    h("div", { class: "sv-info" },
      h("div", { class: "sv-label" }, row.label, row.tip ? tip(row.tip) : null,
        h("span", { class: "sv-saved", "aria-live": "polite" })),
      h("div", { class: "sv-desc", text: row.desc }),
      rowMeta(row),
      h("div", { class: "sv-err", role: "alert" })),
    h("div", { class: "sv-ctl" }, control(row, (v) => save(row, v))));
}

function showRowError(key, msg) {
  const el = root && root.querySelector('.sv-row[data-key="' + key + '"] .sv-err');
  if (el) el.textContent = msg;
}

function flashSaved(key) {
  const el = root && root.querySelector('.sv-row[data-key="' + key + '"] .sv-saved');
  if (!el) return;
  el.textContent = "Saved";
  el.classList.add("on");
  setTimeout(() => el.classList.remove("on"), 1400);
}

async function save(row, value, { undoable = true } = {}) {
  showRowError(row.key, "");
  const prev = row.value;
  try {
    await request("PUT", "/v1/config", { [row.key]: value });
    row.value = value;
    if (row.source === "default" || row.source === "machine") {
      row.source = JSON.stringify(value) === JSON.stringify(row.default) ? "default" : "machine";
    }
    if (undo) clearTimeout(undo.timer);
    undo = undoable ? { row, prev, timer: setTimeout(() => { undo = null; render(); }, 8000) } : null;
    render();
    flashSaved(row.key);
  } catch (e) {
    render();
    showRowError(row.key, "Not saved: " + e.message);
  }
}

// ---- Repos card: everything per repo in one place ----

function repoTools() {
  return (tools || []).filter((t) => t.repo && (t.found || (t.repos || []).some((r) => r.state !== "off")));
}

function repoToolChip(t, r) {
  const st = ((t.repos || []).find((x) => x.root === r.root) || {}).state || "off";
  const key = t.id + "|" + r.root;
  const on = st === "connected";
  return h("button", {
    type: "button", class: "sv-chip" + (on ? " on" : "") + (st === "stale" ? " stale" : ""),
    "aria-pressed": on ? "true" : "false", disabled: !!toolBusy[key],
    title: on ? "Connected. Click to disconnect." : st === "stale" ? "Needs repair. Click to repair." : "Click to connect " + t.label + " here.",
    onclick: () => toolAction(on ? "disconnect" : "connect", t, r.root),
  }, logoEl(t.id), t.label, st === "stale" ? " (repair)" : "");
}

async function connectEverywhere(t) {
  for (const r of t.repos || []) {
    if (r.state !== "connected") await toolAction("connect", t, r.root);
  }
}

function reposCard() {
  const repos = (schema.repos || []).filter((r) => !query || (r.name + " " + r.root + " repo protect rules claude.md").toLowerCase().includes(query));
  if (query && !repos.length) return null;
  const rtools = repoTools();
  const bulk = rtools.filter((t) => (t.repos || []).some((r) => r.state !== "connected"));
  return h("section", { class: "sv-card", "data-section": "repos" },
    h("header", { class: "sv-card-head" },
      h("h2", { text: "Repos" }),
      h("p", { text: "Each repo AgentCollision has seen. Turn protection off for a repo, add the house rules to its CLAUDE.md, and choose which per-repo tools are connected there." })),
    !schema.repos || !schema.repos.length
      ? h("div", { class: "sv-row" }, h("div", { class: "sv-desc", text: "Repos appear here once a chat has worked in them." }))
      : [
        bulk.length ? h("div", { class: "sv-bulk" }, h("span", { class: "sv-hint", text: "Connect in every repo:" }),
          bulk.map((t) => h("button", { type: "button", class: "btn btn-sm", onclick: () => connectEverywhere(t) }, logoEl(t.id), t.label))) : null,
        repos.map((r) => h("div", { class: "sv-repo", "data-root": r.root },
          h("div", { class: "sv-info" },
            h("div", { class: "sv-label", text: r.name }, h("span", { class: "sv-saved", "aria-live": "polite" })),
            h("div", { class: "sv-desc sv-mono", text: r.root }),
            rtools.length ? h("div", { class: "sv-chips" }, rtools.map((t) => repoToolChip(t, r))) : null,
            h("div", { class: "sv-err", role: "alert" })),
          h("div", { class: "sv-ctl sv-repo-ctl" },
            r.etiquette === undefined ? null : r.etiquette
              ? h("span", { class: "sv-ok" }, "Rules in CLAUDE.md")
              : h("button", { type: "button", class: "btn btn-sm", onclick: () => addRules(r) }, "Add CLAUDE.md rules"),
            h("label", { class: "sv-inline" },
              h("span", { text: "Protected" }),
              (() => {
                const sw = h("button", { type: "button", class: "sv-switch", role: "switch", "aria-label": "Protect " + r.name,
                  "aria-checked": r.disabled ? "false" : "true",
                  onclick: () => setRepoProtection(r, sw.getAttribute("aria-checked") !== "true") });
                return sw;
              })())))),
      ]);
}

function repoFeedback(r, msg, ok) {
  const row = root.querySelector('.sv-repo[data-root="' + CSS.escape(r.root) + '"]');
  if (!row) return;
  if (ok) {
    const el = row.querySelector(".sv-saved");
    el.textContent = msg; el.classList.add("on");
    setTimeout(() => el.classList.remove("on"), 1400);
  } else {
    row.querySelector(".sv-err").textContent = msg;
  }
}

async function setRepoProtection(r, enabled) {
  try {
    await request("PUT", "/v1/repos/protection", { root: r.root, enabled });
    r.disabled = !enabled;
    render();
    repoFeedback(r, enabled ? "Protected" : "Turned off", true);
  } catch (e) {
    render();
    repoFeedback(r, "Not saved: " + e.message, false);
  }
}

async function addRules(r) {
  try {
    await request("POST", "/v1/repos/etiquette", { root: r.root });
    r.etiquette = true;
    render();
    repoFeedback(r, "Added", true);
  } catch (e) {
    repoFeedback(r, "Not saved: " + e.message, false);
  }
}

// ---- Connected tools card ----

const STATE_LABEL = { connected: "Connected", stale: "Needs repair", off: "Not connected" };
const STRENGTH_LABEL = { hooks: "Blocks edits", mcp: "Asks first", advisory: "Warns at commit" };

function statePill(state, found) {
  const st = state === "off" && !found ? "missing" : state;
  const text = st === "missing" ? "Not installed" : STATE_LABEL[state] || state;
  return h("span", { class: "sv-state sv-state-" + st }, text);
}

function toolButtons(t, root, state) {
  const key = t.id + "|" + (root || "");
  if (toolBusy[key]) return h("span", { class: "sv-hint", text: "Working..." });
  const connect = h("button", { type: "button", class: "btn btn-sm", onclick: () => toolAction("connect", t, root) },
    state === "stale" ? "Repair" : state === "connected" ? "Reconnect" : "Connect");
  const disconnect = state === "off" ? null
    : h("button", { type: "button", class: "sv-link", onclick: () => toolAction("disconnect", t, root) }, "Disconnect");
  return [disconnect, state === "connected" ? null : connect];
}

async function toolAction(kind, t, root) {
  const key = t.id + "|" + (root || "");
  toolBusy[key] = true;
  delete toolNotes[key];
  render();
  try {
    const res = await request("POST", "/v1/tools/" + kind, root ? { id: t.id, root } : { id: t.id });
    if (kind === "connect" && res && res.note) toolNotes[key] = res.note;
    const j = await request("GET", "/v1/tools");
    tools = j.tools;
  } catch (e) {
    toolNotes[key] = "Not done: " + e.message;
  } finally {
    delete toolBusy[key];
    render();
  }
}

function logoEl(id) {
  const html = agentLogo(id, 22);
  if (!html) return null;
  const span = document.createElement("span");
  span.innerHTML = html;
  return span.firstElementChild;
}

function toolSummary(t) {
  if (!t.repo) return t.state;
  const rs = t.repos || [];
  if (rs.some((r) => r.state === "stale")) return "stale";
  return rs.some((r) => r.state === "connected") ? "connected" : "off";
}

function toolsBlock() {
  if (!tools) return null;
  const q = query;
  const matchesQ = (t) => !q || (t.label + " " + t.detail + " tools connect").toLowerCase().includes(q);
  const relevant = (t) => t.found || toolSummary(t) !== "off";
  const main = tools.filter((t) => relevant(t) && matchesQ(t));
  const other = tools.filter((t) => !relevant(t) && matchesQ(t));
  if (!main.length && !other.length) return null;
  const row = (t) => {
    const key = t.id + "|";
    const st = toolSummary(t);
    const rs = t.repos || [];
    const on = rs.filter((r) => r.state === "connected").length;
    return h("div", { class: "sv-row", "data-tool": t.id },
      h("div", { class: "sv-info" },
        h("div", { class: "sv-label" }, logoEl(t.id), t.label, statePill(st, t.found)),
        h("div", { class: "sv-desc" }, h("span", { class: "sv-pill sv-pill-" + t.strength, text: STRENGTH_LABEL[t.strength] || t.strength }), " ", t.detail),
        t.repo ? h("div", { class: "sv-desc", text: rs.length ? "Connected in " + on + " of " + rs.length + " repo" + (rs.length === 1 ? "" : "s") + ". Choose repos under Repos below." : "Repos appear once a chat has worked in them." }) : null,
        !t.repo && st === "stale" ? h("div", { class: "sv-meta" }, h("span", { class: "sv-lock", text: "Its connection points at an AgentCollision version an upgrade removed, so this tool is not protected. Repair fixes it." })) : null,
        toolNotes[key] ? h("div", { class: "sv-note", text: toolNotes[key] }) : null),
      h("div", { class: "sv-ctl" }, t.repo ? null : toolButtons(t, "", t.state)));
  };
  return h("section", { class: "sv-card", "data-section": "tools" },
    h("header", { class: "sv-card-head" },
      h("h2", { text: "Connected tools" }),
      h("p", { text: "The AI coding tools AgentCollision protects. All of them share the same leases, so a chat in one tool waits for a chat in another." })),
    main.map(row),
    other.length ? (showOtherTools || q
      ? other.map(row)
      : h("div", { class: "sv-row sv-more-row" }, h("button", { type: "button", class: "sv-link", onclick: () => { showOtherTools = true; render(); } },
        "Show " + other.length + " other tool" + (other.length === 1 ? "" : "s") + " (not on this Mac)"))) : null);
}

// ---- Undo and restart ----

function undoBar() {
  if (!undo) return null;
  const { row, prev } = undo;
  return h("div", { class: "sv-undo", role: "status" },
    h("span", { text: row.label + ": " + describeValue(row, row.value) }),
    h("button", { type: "button", class: "sv-link", onclick: () => { clearTimeout(undo.timer); undo = null; save(row, prev, { undoable: false }); } },
      "Undo"));
}

function needsRestart() {
  return (schema.settings || []).filter((r) => r.applies === "restart" && !r.read_only &&
    JSON.stringify(r.value) !== JSON.stringify(loadedValues[r.key]));
}

function restartCard() {
  const pending = needsRestart();
  if (!pending.length && !restarting) return null;
  const working = restarting === "working";
  const failed = restarting && !working ? restarting : "";
  const what = pending.map((r) => r.label).join(", ");
  return h("section", { class: "sv-restart" + (failed ? " failed" : "") },
    h("div", { class: "sv-health-txt" },
      h("b", { text: working ? "Restarting AgentCollision..." : failed ? "Restart did not finish" : "Restart to apply " + (pending.length === 1 ? "1 change" : pending.length + " changes") }),
      h("span", { text: working ? "Chats keep their leases; this takes a few seconds." : failed || what })),
    h("div", { class: "sv-ctl" },
      working ? null : h("button", { type: "button", class: "btn btn-sm btn-primary", onclick: restartDaemon }, failed ? "Try again" : "Restart now")));
}

async function daemonPID() {
  const r = await fetch("/v1/info", { cache: "no-store" });
  if (!r.ok) throw new Error("HTTP " + r.status);
  return (await r.json()).pid;
}

// restartDaemon asks the daemon to restart itself, then waits for a NEW
// process (a different pid) before reloading, so the page never reports a
// restart that did not happen.
async function restartDaemon() {
  restarting = "working";
  render();
  try {
    const before = await daemonPID();
    await request("POST", "/v1/daemon/restart");
    const deadline = Date.now() + 30000;
    for (;;) {
      await new Promise((ok) => setTimeout(ok, 1000));
      let pid = null;
      try { pid = await daemonPID(); } catch { /* down while it restarts */ }
      if (pid && pid !== before) break;
      if (Date.now() > deadline) throw new Error("AgentCollision did not come back within 30 seconds. Run agc heal in a terminal.");
    }
    restarting = null;
    loadedValues = {};
    await renderSettingsV2();
  } catch (e) {
    restarting = e.message;
    render();
  }
}

// ---- Health summary ----

function staleTargets() {
  const out = [];
  for (const t of tools || []) {
    if (!t.repo && t.state === "stale") out.push([t, ""]);
    for (const r of t.repos || []) if (r.state === "stale") out.push([t, r.root]);
  }
  return out;
}

async function fixAll() {
  for (const [t, r] of staleTargets()) await toolAction("connect", t, r);
}

function healthCard() {
  if (query) return null;
  const connected = (tools || []).filter((t) => toolSummary(t) === "connected").length;
  const stale = staleTargets().length;
  const auto = (schema.settings || []).find((r) => r.key === "auto_start");
  const autoOff = auto && auto.value === false && !auto.read_only;
  const syncLost = !!(info && info.team_auth_rejected);
  const ok = stale === 0 && connected > 0 && !autoOff && !syncLost;
  const bits = [connected ? connected + " tool" + (connected === 1 ? "" : "s") + " connected" : "no tools connected yet"];
  if (syncLost) bits.push("team sync stopped");
  if (stale) bits.push(stale + " need" + (stale === 1 ? "s" : "") + " repair");
  if (autoOff) bits.push("does not start at login");
  return h("section", { class: "sv-health " + (ok ? "ok" : "warn") },
    h("span", { class: "sv-health-dot" }),
    h("div", { class: "sv-health-txt" },
      h("b", { text: ok ? "Your chats are protected" : "Needs attention" }),
      h("span", { text: "Running · " + bits.join(" · ") }),
      syncLost ? h("span", { class: "sv-health-detail" },
        "Your team no longer accepts this computer's key" + (info.last_push_at ? " (last synced " + new Date(info.last_push_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) + ")" : "") +
        ". Chats here are still protected; other computers can't see them. Run ", h("code", { text: "agc login" }), " to reconnect.") : null),
    h("div", { class: "sv-ctl" },
      stale ? h("button", { type: "button", class: "btn btn-sm btn-primary", onclick: fixAll }, "Repair " + (stale === 1 ? "it" : "all " + stale)) : null,
      autoOff ? h("button", { type: "button", class: "btn btn-sm", onclick: () => save(auto, true) }, "Start at login") : null));
}

// ---- History card ----

function messagesBlock() {
  const m = schema.messages;
  if (!m || (query && !"messages history record coordination threads".includes(query))) return null;
  const since = m.first && !m.first.startsWith("0001") ? new Date(m.first).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : null;
  const size = m.bytes > 1048576 ? (m.bytes / 1048576).toFixed(1) + " MB" : Math.max(1, Math.round(m.bytes / 1024)) + " KB";
  return h("div", { class: "sv-row" },
    h("div", { class: "sv-info" },
      h("div", { class: "sv-label" }, "Messages between chats", tip("Every message chats send each other is kept on this machine, full text, threaded. Messages that were never delivered are cleaned up after a while; delivered ones are kept.")),
      h("div", { class: "sv-desc" },
        m.messages ? fmtNumber(m.messages) + " messages in " + fmtNumber(m.threads) + " threads" + (since ? " since " + since : "") + " (" + size + ")." : "No messages yet.")),
    h("div", { class: "sv-ctl" }, h("a", { href: "#", class: "btn btn-sm", "data-page-link": "coordination" }, "Open Coordination")));
}

// ---- page ----

const TABS = [
  { id: "tools", title: "Tools", sections: [] },
  { id: "protection", title: "Protection", sections: ["background", "coordination", "protection"] },
  { id: "workspaces", title: "Workspaces", sections: ["workspaces", "repo_context"] },
  { id: "advanced", title: "Advanced", sections: ["history", "advanced"] },
];

function setTab(id) {
  tab = id;
  history.replaceState(null, "", location.pathname + location.search + "#" + id);
  render();
}

// applyTarget opens the tab named by `target`, or the tab holding the
// setting it names, and scrolls to and highlights that setting.
function applyTarget() {
  if (!target || !schema) return;
  const t = target;
  target = "";
  if (TABS.some((x) => x.id === t)) { tab = t; query = ""; render(); return; }
  const row = (schema.settings || []).find((r) => r.key === t);
  const owner = row && TABS.find((x) => x.sections.includes(row.section));
  if (!owner) return;
  tab = owner.id;
  query = "";
  render();
  const el = root.querySelector('.sv-row[data-key="' + t + '"]');
  if (!el) return;
  el.scrollIntoView({ block: "center" });
  el.classList.add("sv-flash");
  setTimeout(() => el.classList.remove("sv-flash"), 2000);
}

// openSettingsAt is for in-app links: navigate to Settings, then call this.
export function openSettingsAt(id) {
  target = id;
  history.replaceState(null, "", location.pathname + location.search + "#" + id);
  applyTarget();
}

function render() {
  if (!root || !schema) return;
  if (!TABS.some((t) => t.id === tab)) tab = "tools";
  const prevFocus = document.activeElement && document.activeElement.closest && document.activeElement.closest("[data-key]");
  const focusKey = prevFocus && prevFocus.getAttribute("data-key");
  root.innerHTML = "";
  const hc = healthCard();
  if (hc) root.appendChild(hc);
  const rc0 = query ? null : restartCard();
  if (rc0) root.appendChild(rc0);
  const search = h("input", { type: "search", class: "sv-search", placeholder: "Search all settings", "aria-label": "Search settings", value: query });
  search.addEventListener("input", () => { query = search.value.trim().toLowerCase(); render(); const s = root.querySelector(".sv-search"); s.focus(); s.setSelectionRange(s.value.length, s.value.length); });
  const tabBar = h("div", { class: "sv-tabs", role: "tablist", "aria-label": "Settings sections" },
    TABS.map((t) => h("button", { type: "button", role: "tab", class: !query && tab === t.id ? "on" : null,
      "aria-selected": !query && tab === t.id ? "true" : "false", onclick: () => { query = ""; setTab(t.id); } }, t.title)));
  root.appendChild(h("div", { class: "sv-top" }, tabBar, search));

  // Searching looks across every tab; otherwise show the chosen tab.
  const visible = query ? TABS : TABS.filter((t) => t.id === tab);
  let shown = 0;
  for (const t of visible) {
    if (t.id === "tools") {
      const tb = toolsBlock();
      if (tb) { root.appendChild(tb); shown++; }
      const rc = reposCard();
      if (rc) { root.appendChild(rc); shown++; }
    }
    for (const sec of schema.sections.filter((x) => t.sections.includes(x.id))) {
      const rows = schema.settings.filter((r) => r.section === sec.id && matches(r)).map(settingRow);
      const extra = sec.id === "history" ? messagesBlock() : null;
      if (!rows.length && !extra) continue;
      shown += rows.length + (extra ? 1 : 0);
      root.appendChild(h("section", { class: "sv-card", "data-section": sec.id },
        h("header", { class: "sv-card-head" }, h("h2", { text: sec.title }), h("p", { text: sec.desc })),
        extra, rows));
    }
  }
  if (!shown) root.appendChild(h("p", { class: "sv-empty", text: 'No settings match "' + query + '".' }));
  const ub = undoBar();
  if (ub) root.appendChild(ub);
  // The Team Sync panel sits outside this renderer; it belongs with Advanced.
  const team = document.getElementById("settings-team-panel");
  if (team) team.classList.toggle("sv-hidden", !query && tab !== "advanced");
  if (focusKey) {
    const el = root.querySelector('[data-key="' + focusKey + '"] .sv-switch, [data-key="' + focusKey + '"] input');
    if (el) el.focus({ preventScroll: true });
  }
}

// Follow #tab and #setting_key links without a reload.
window.addEventListener("hashchange", () => {
  const id = (location.hash || "").replace("#", "");
  if (root && id && id !== tab) { target = id; applyTarget(); }
});

export async function renderSettingsV2() {
  root = document.getElementById("settings-v2");
  if (!root) return;
  if (!schema) root.innerHTML = '<p class="sv-empty">Loading settings...</p>';
  try {
    const [sc, tj, inf] = await Promise.all([
      request("GET", "/v1/config/schema"),
      request("GET", "/v1/tools").catch(() => null),
      request("GET", "/v1/info").catch(() => null),
    ]);
    schema = sc;
    info = inf;
    tools = tj && tj.tools ? tj.tools : null;
    // Kept across visits to this page; only a restart resets it.
    if (!Object.keys(loadedValues).length) loadedValues = Object.fromEntries((sc.settings || []).map((r) => [r.key, r.value]));
    render();
    applyTarget();
  } catch (e) {
    root.innerHTML = "";
    root.appendChild(h("p", { class: "sv-empty", text: "Could not load settings from AgentCollision on this computer (" + e.message + "). Is agc daemon running?" }));
  }
}
